import React, { useRef } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Text,
} from '@chakra-ui/react';
import { FaMapMarkerAlt } from 'react-icons/fa';
import HousePrices from '../components/home/sections/HousePrices';
import useHousePrices from '../hooks/useHousePrices';
import { usePreparePropertyChartData } from '../hooks/usePreparePropertyChartData';
import { useDerivedSales } from '../hooks/useDerivedSales';
import { useCalculateEstimatedCurrentPrice } from '../hooks/useCalculateEstimatedCurrentPrice';
import { useGetChartData } from '../hooks/useGetChartData';
import useSearchState from '../hooks/useSearch';
import { useNavigateViaHash } from '../hooks/useNavigateViaHash';

export function HousePricesSection() {
  const housePricesRef = useRef(null);
  useNavigateViaHash(housePricesRef, 'house-prices');

  const { searchState } = useSearchState();
  const { validatedAddress } = searchState;
  const postcode = validatedAddress?.postcode;

  const { data: housePricesData, isLoading: isLoadingHousePrices } = useHousePrices(postcode);
  const sales = housePricesData?.sales ?? [];

  const { data: derivedData, isLoading: isLoadingDerivedData } = useDerivedSales({
    validatedAddress,
  });

  const safeDerivedData =
    isLoadingDerivedData || !derivedData
      ? { currentPropertySales: null, areaSales: null, propertyAddress: null }
      : derivedData;
  const { currentPropertySales, areaSales, propertyAddress } = safeDerivedData;

  const { data: chartData, isLoading: isLoadingChartData } = useGetChartData({
    validatedAddress,
    postcode,
  });

  const { data: propertyChartData, isLoading: isLoadingPropertyChartData } =
    usePreparePropertyChartData({
      validatedAddress,
      postcode,
    });

  const { data: price } = useCalculateEstimatedCurrentPrice({
    validatedAddress,
  });

  const isLoading =
    isLoadingHousePrices ||
    isLoadingDerivedData ||
    isLoadingChartData ||
    isLoadingPropertyChartData;

  return (
    <Card ref={housePricesRef} variant='outline' mb={8} bg='brand.section'>
      <CardHeader>
        <HStack spacing={2}>
          <Icon as={FaMapMarkerAlt} boxSize={6} color='brand.primary' />
          <Heading size='md' color='brand.text'>
            House Prices
          </Heading>
        </HStack>
      </CardHeader>
      <CardBody>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <HousePrices
            housePriceData={housePricesData}
            isLoading={isLoading}
            postcode={postcode}
            validatedAddress={validatedAddress}
            calculatedEstimatedPrice={price ?? 0}
            propertyChartData={propertyChartData}
            sales={sales}
            propertyAddress={propertyAddress}
            currentPropertySales={currentPropertySales}
            areaSales={areaSales}
            chartData={chartData}
          />
        </SimpleGrid>
      </CardBody>
    </Card>
  );
}

export default HousePricesSection;
